import { getApiConfig } from "../config";
import { proxyFetch } from "./proxyFetch";

const BATCH_PATH = "/marketdata/option-pop-batch";
const CLIENT_CACHE_MS = 6 * 60 * 60 * 1000;
const CLIENT_MISS_CACHE_MS = 15 * 60 * 1000;

function marketdataBatchUrl(): string | null {
  const { baseUrl } = getApiConfig();
  if (!baseUrl) return null;
  if (baseUrl === window.location.origin) return BATCH_PATH;
  return `${baseUrl}${BATCH_PATH}`;
}

export interface MarketdataOptionPopEntry {
  pop: number | null;
  cached?: boolean;
  stale?: boolean;
  source?: string;
}

export interface MarketdataOptionPopBatchResult {
  ok: boolean;
  pops: Record<string, MarketdataOptionPopEntry>;
  creditsUsed: number;
  symbolsFetched: number;
  dailyCreditsRemaining?: number;
  code?: string;
  message?: string;
}

const clientPopCache = new Map<
  string,
  { pop: number | null; at: number; miss?: boolean }
>();

function marketdataHeaders(): HeadersInit {
  return {
    Accept: "application/json",
    "Content-Type": "application/json",
  };
}

function normalizeOptionSymbol(symbol: string): string {
  return String(symbol || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");
}

export function getCachedMarketdataOptionPop(symbol: string): number | null | undefined {
  const key = normalizeOptionSymbol(symbol);
  if (!key) return undefined;
  const hit = clientPopCache.get(key);
  if (!hit) return undefined;
  const ttl = hit.miss ? CLIENT_MISS_CACHE_MS : CLIENT_CACHE_MS;
  if (Date.now() - hit.at > ttl) {
    clientPopCache.delete(key);
    return undefined;
  }
  return hit.pop;
}

export function rememberMarketdataOptionPop(
  symbol: string,
  pop: number | null | undefined,
  miss = false,
): void {
  const key = normalizeOptionSymbol(symbol);
  if (!key) return;
  clientPopCache.set(key, {
    pop: pop != null && Number.isFinite(pop) ? pop : null,
    at: Date.now(),
    miss: miss || pop == null || !Number.isFinite(pop),
  });
}

/** Fetch POP (1 − |delta|) for specific OCC option symbols via the proxy cache. */
export async function fetchMarketdataOptionPopBatch(
  symbols: string[],
  force = false,
): Promise<MarketdataOptionPopBatchResult> {
  const empty: MarketdataOptionPopBatchResult = {
    ok: false,
    pops: {},
    creditsUsed: 0,
    symbolsFetched: 0,
  };

  const normalized = [
    ...new Set(symbols.map(normalizeOptionSymbol).filter(Boolean)),
  ];
  if (!normalized.length) {
    return { ...empty, code: "invalid_params" };
  }

  const batchUrl = marketdataBatchUrl();
  if (!batchUrl) {
    return { ...empty, code: "proxy_unconfigured" };
  }

  if (!force) {
    const cachedOnly: Record<string, MarketdataOptionPopEntry> = {};
    const missing: string[] = [];
    for (const sym of normalized) {
      const cached = getCachedMarketdataOptionPop(sym);
      if (cached !== undefined) {
        cachedOnly[sym] = { pop: cached, cached: true, source: "marketdata" };
      } else {
        missing.push(sym);
      }
    }
    if (!missing.length) {
      return {
        ok: true,
        pops: cachedOnly,
        creditsUsed: 0,
        symbolsFetched: 0,
      };
    }
  }

  try {
    const res = await proxyFetch(batchUrl, {
      method: "POST",
      headers: marketdataHeaders(),
      body: JSON.stringify({ symbols: force ? normalized : normalized.filter((sym) => getCachedMarketdataOptionPop(sym) === undefined), force }),
      cache: "no-store",
    });

    const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    if (!res.ok) {
      return {
        ...empty,
        code: String(data.code ?? "http_error"),
        message: String(data.message ?? `${res.status} ${res.statusText}`),
      };
    }

    const pops =
      data.pops && typeof data.pops === "object"
        ? (data.pops as Record<string, MarketdataOptionPopEntry>)
        : {};

    for (const [sym, entry] of Object.entries(pops)) {
      rememberMarketdataOptionPop(sym, entry?.pop ?? null, entry?.pop == null);
    }

    if (!force) {
      for (const sym of normalized) {
        if (pops[sym]) continue;
        const cached = getCachedMarketdataOptionPop(sym);
        if (cached !== undefined) {
          pops[sym] = { pop: cached, cached: true, source: "marketdata" };
        }
      }
    }

    return {
      ok: Boolean(data.ok),
      pops,
      creditsUsed: Number(data.creditsUsed) || 0,
      symbolsFetched: Number(data.symbolsFetched) || 0,
      dailyCreditsRemaining:
        data.dailyCreditsRemaining != null ? Number(data.dailyCreditsRemaining) : undefined,
      code: data.code != null ? String(data.code) : undefined,
      message: data.message != null ? String(data.message) : undefined,
    };
  } catch (err) {
    return {
      ...empty,
      code: "network_error",
      message: err instanceof Error ? err.message : "fetch failed",
    };
  }
}
